import { CdnContext } from '@/contexts/CdnContext';
import { ChatRoomDisplayContext } from '@/contexts/ChatRoomDisplayContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';

import { getGameState } from '@/modules/games/selector';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
    getMainChatState,
    getMainGiftState,
    getMainUserState,
} from '../modules/main/selector';

import { useTranslation } from 'react-i18next';
import { useCurrentTime } from './useCurrentTime';
import { GiftInfo } from './useGift';
import { useNavigatorOnLine } from './useNavigatorOnLine';

enum MSG_TYPE {
    Login = 1,
    JoinRoom,
    RenewToken,
    Gift,
    Heartbeat,
    Error,
    LeaveRoom,
    SendMsg,
    ReceiveRoomMsg,
    ChatHeartbeat,
}

enum ERROR_TYPE {
    Success = 0,
    WrongInputFormat,
    FailToVerifyToken, //Fail To Verify Token (Before Login) -> (for responseMsgType=1)
    WrongInputBeforeLogin, //Wrong Input Before Login -> (for responseMsgType=6)
    WrongInputAfterLogin, //Wrong Input After Login -> (for responseMsgType=6)
    FailtoJoinRoomInvalidHostId, //Fail to Join Room (invalid host id) -> (for responseMsgType=2) -> invalid host id -> negative value
    FailToRenewToken, //Fail to renew Token (for responseMsgType=3)
    ReachMaxConnection, //Reach Max Connection (for responseMsgType=6)
    DuplicateLogin, //Duplicate Login (for responseMsgType = 6)
    FailToJoinRoomNoHostId, //Fail to Join Room (no host id) -> (for responseMsgType=2) ## Server 未有呢個 host ##
    FailToSendMsgLastBetTimeError, //Fail to Send Msg (last bet time error) -> (for responseMsgType=8)
    FailToSendMsgExceedSendLimit, //Fail to Send Msg (exceed send limit) -> (for responseMsgType=8)
    FailToSendMsgExceedTextLimit, //Fail to Send Msg (exceed text limit) -> (for responseMsgType=8)
    FailToSendMsgUnexpectedError,
}

export type ChatInfo = {
    message: string;
    self?: boolean;
    key?: string;
    error?: boolean;
};

const RETRY_MAX_COUNT = 5;
const useChatRoom = () => {
    const {
        Username,
        LoginToken,
        PlayerID,
        IsTrial,
        IsInternalTest,
        HostListInited,
    } = useSelector(getMainUserState);
    const { notToReconnect } = useSelector(getGameState);
    const { chatWs } = useContext(CdnContext);
    const { lobbyGiftEnable } = useSelector(getMainGiftState);
    const { lobbyChatEnable } = useSelector(getMainChatState);
    const giftSocket = useRef<WebSocket>();
    const keepAliveRef = useRef<NodeJS.Timeout>();
    const [allowSendMsg, setAllowSendMsg] = useState<boolean>(false);
    const [pendingGifts, setPendingGifts] = useState<Array<GiftInfo>>([]);
    const [giftsDisplay, setGiftsDisplay] = useState<Array<GiftInfo>>([]);
    const showOtherPlayerGiftRef = useRef<boolean>(true);
    const { currentTimeStamp } = useCurrentTime();

    const retryCount = useRef<number>(0);
    const notToReconnectRef = useRef<boolean>(false);
    const joinedRoomHostId = useRef<number>();
    const {
        showOtherPlayerGift,
        isChatPanelEnabled,
        setLoginReady,
        joinRoom,
        chatEnabled,
        giftEnabled,
    } = useContext(ChatRoomDisplayContext);
    const onlineState = useNavigatorOnLine();

    useEffect(() => {
        showOtherPlayerGiftRef.current = showOtherPlayerGift;
        if (!showOtherPlayerGift && giftsDisplay.length > 0) {
            setGiftsDisplay(list => list?.filter(g => g.self));
        }
        if (!showOtherPlayerGift && pendingGifts.length > 0) {
            setPendingGifts(list => list?.filter(g => g.self));
        }
    }, [showOtherPlayerGift]);

    useEffect(() => {
        sendGiftMessage(MSG_TYPE.RenewToken);
    }, [LoginToken]);
    useEffect(() => {
        notToReconnectRef.current = notToReconnect;
        if (notToReconnect) {
            socketClose();
        }
    }, [notToReconnect]);

    useEffect(() => {
        if (
            lobbyGiftEnable != undefined &&
            lobbyChatEnable != undefined &&
            (lobbyGiftEnable || lobbyChatEnable) &&
            chatWs &&
            Username &&
            LoginToken &&
            PlayerID &&
            HostListInited
        ) {
            socketCreate();
            return () => socketClose();
        }
    }, [chatWs, lobbyGiftEnable, lobbyChatEnable, HostListInited]);

    const socketCreate = () => {
        if (notToReconnectRef.current) {
            return;
        }
        giftSocket.current = new WebSocket(chatWs);
        giftSocket.current.onopen = () => {
            console.log('chatroom::gift::onopen');
            retryCount.current = 0;
            sendGiftMessage(MSG_TYPE.Login);
        };
        giftSocket.current.onmessage = event => {
            const data = JSON.parse(event.data);
            console.log('chatroom::gift::message', data);
            if (
                data.response_code == ERROR_TYPE.Success ||
                data.response_msg_type == MSG_TYPE.SendMsg
            ) {
                switch (data.response_msg_type) {
                    case MSG_TYPE.Login:
                        setLoginReady(true);
                        setAllowSendMsg(true);
                        if (joinedRoomHostId.current) {
                            sendGiftMessage(
                                MSG_TYPE.JoinRoom,
                                joinedRoomHostId.current
                            );
                        }
                        break;
                    case MSG_TYPE.Heartbeat:
                        setAllowSendMsg(true);
                        giftEvent(data.data);
                        if (data.data) {
                            const healthyList =
                                data.data.chat_control_server_healthy_list;
                            if (healthyList) {
                                chatServerChecking({
                                    is_chat_control_server_healthy:
                                        healthyList.includes(
                                            joinedRoomHostId.current
                                        ),
                                });
                            }
                        }
                        keepAliveRef.current &&
                            clearTimeout(keepAliveRef.current);
                        keepAliveRef.current = setTimeout(
                            () => {
                                setAllowSendMsg(false);
                            },
                            60 * 1000 * 3
                        );
                        break;
                    case MSG_TYPE.Error:
                        break;
                    case MSG_TYPE.Gift:
                        giftEvent(data.data);
                        break;
                    case MSG_TYPE.SendMsg:
                        lastBetChecking(data);
                        break;
                    case MSG_TYPE.ReceiveRoomMsg:
                        addMessage(data.data);
                        break;
                    case MSG_TYPE.JoinRoom:
                        chatServerChecking(data);
                        // if (data.history) {
                        //     const history = data.history;
                        //     if (Array.isArray(history)) {
                        //         history.forEach(msgData => {
                        //             addMessage(msgData);
                        //         });
                        //     }
                        // }
                        break;
                }
            }
        };
        giftSocket.current.onclose = () => {
            if (retryCount.current < RETRY_MAX_COUNT) {
                if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                    setAllowSendMsg(false);
                    setTimeout(() => {
                        if (
                            giftSocket.current?.readyState == WebSocket.CLOSED
                        ) {
                            retryCount.current += 1;
                            socketCreate();
                        }
                    }, 10000);
                }
            }
        };
    };

    const sendGiftMessage = (msgType: MSG_TYPE, hostId?: number) => {
        if (
            giftSocket.current &&
            giftSocket.current.readyState == WebSocket.OPEN
        ) {
            let msg = {
                request_msg_type: msgType,
                player_name: Username,
                player_id: PlayerID,
                token: LoginToken.slice(0, 16),
                join_host_id: hostId ?? 0,
            };
            if (msgType == MSG_TYPE.JoinRoom) {
                msg = {
                    ...msg,
                    ...{
                        enable_gift: giftEnabled,
                        enable_chat: chatEnabled,
                    },
                };
            }
            console.log('chatroom::gift::send', msg);
            giftSocket.current?.send(JSON.stringify(msg));
        }
    };

    const socketClose = () => {
        giftSocket.current?.close();
    };

    const onFocus = () => {
        // const ts = localStorage.getItem(LocalStorageKey.LastBetTs);
        // if (!IsTrial && Number(ts) + 5 * 60 * 1000 >= currentTimeStamp()-8*3600*1000) {
        //     setDisable(false);
        // } else {
        //     if (IsTrial) {
        //         addErrorMessage(t('trial_account'));
        //     } else {
        //         addErrorMessage(t('chat_room.place_bet'));
        //     }
        //     setDisable(true);
        // }
    };

    const giftEvent = (giftJson: {
        gift_id: number;
        host_id: number;
        player_id: string;
        display_interval: number;
    }) => {
        if (giftJson.gift_id != undefined) {
            if (IsInternalTest || (!IsInternalTest && giftJson.gift_id > 0)) {
                const gift = {
                    giftId: giftJson.gift_id,
                    display_interval: giftJson.display_interval,
                    hostId: giftJson.host_id,
                    self: giftJson.player_id == PlayerID.toString(),
                    key: uuidv4(),
                };
                if (giftJson.player_id == PlayerID.toString()) {
                    // setGiftsDisplay(list => list?.concat(gift));
                    setPendingGifts(list => list?.concat(gift));
                } else if (showOtherPlayerGiftRef.current) {
                    setPendingGifts(list => list?.concat(gift));
                }
            }
        }
    };
    // //=================== fake gift ================
    // const tmp = useRef<number>(0);
    // useEffect(() => {
    //     const isDevelopment = process.env.NODE_ENV === 'development';
    //     let id: NodeJS.Timeout;
    //     if (joinedRoomHostId && isDevelopment) {
    //         id = setInterval(() => {
    //             giftEvent({
    //                 gift_id: (tmp.current % 8) + 1,
    //                 host_id: 901,
    //                 display_interval: 3000,
    //                 player_id: '21616',
    //             });
    //             tmp.current++;
    //         }, 200);
    //     }
    //     return () => {
    //         if (id) clearInterval(id);
    //     };
    // }, [joinedRoomHostId, giftEvent]);
    // //===================

    useEffect(() => {
        if (pendingGifts.length > 2) {
            //if waiting list long, slow down update frequency
            if (giftsDisplay.length <= 8) {
                let newPendingGifts = pendingGifts.concat([]);
                const gift = newPendingGifts.splice(
                    0,
                    10 - giftsDisplay.length
                );
                if (gift && gift.length > 0) {
                    setGiftsDisplay(list => list.concat(gift));
                    setPendingGifts(newPendingGifts);
                }
            }
        } else if (giftsDisplay.length < 10 && pendingGifts.length > 0) {
            let newPendingGifts = pendingGifts.concat([]);
            const gift = newPendingGifts.splice(0, 10 - giftsDisplay.length);
            if (gift && gift.length > 0) {
                setGiftsDisplay(list => list.concat(gift));
                setPendingGifts(newPendingGifts);
            }
        }
    }, [pendingGifts, giftsDisplay]);

    useEffect(() => {
        if (joinRoom) {
            setGiftsDisplay([]);
            setPendingGifts([]);
            setMsgDisplay([]);
            allowInput.current = true;
            joinedRoomHostId.current = joinRoom.hostId;

            if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                retryCount.current = 0;
                socketCreate();
            }
            sendGiftMessage(MSG_TYPE.JoinRoom, joinRoom.hostId);
        } else {
            joinedRoomHostId.current = undefined;
            sendGiftMessage(MSG_TYPE.LeaveRoom);
        }
    }, [joinRoom]);
    useEffect(() => {
        if (onlineState) {
            if (giftSocket.current?.readyState == WebSocket.CLOSED) {
                retryCount.current = 0;
                socketCreate();
            }
        }
    }, [onlineState]);

    //========================== Chat ===================
    const [placeholder, setPlaceholder] = useState('');
    const [showChatPanel, setShowChatPanel] = useState(false);
    const [disable, setDisable] = useState(false);
    const [hideSendBtn, setHideSendBtn] = useState(false);
    const { chat: chatConfig } = useContext(CommonConfigContext);
    const [msgDisplay, setMsgDisplay] = useState<Array<ChatInfo>>([]);
    const [chatTime, setChatTime] = useState<number>(300);
    const { t, i18n } = useTranslation();
    const allowInput = useRef<boolean>(true);
    const [inputIntervalTime, setInputIntervalTime] = useState<number>(2000);
    const [chatConnected, setChatConnected] = useState<boolean>(false);

    useEffect(() => {
        if (chatConfig) {
            setInputIntervalTime(chatConfig.inputIntervalTime ?? 2000);
            setChatTime(
                chatConfig.lastBetTime ? chatConfig.lastBetTime / 1000 : 300
            );
        }
    }, [chatConfig]);

    useEffect(() => {
        if (IsTrial) {
            setDisable(true);
            setHideSendBtn(true);
            setPlaceholder(t('trial_account'));
        } else if (!chatConnected || !allowSendMsg) {
            setDisable(true);
            setHideSendBtn(false);
            setPlaceholder(t('chat_room.chat_room_connecting'));
        } else {
            setDisable(false);
            setHideSendBtn(false);
            setPlaceholder(t('chat_room.chat_room_prompt'));
        }
    }, [IsTrial, allowSendMsg, chatConnected, i18n.language]);

    /**
     * chatEnabled: host list
     * lobbyChatEnable: lobby
     * isChatRoomEnabled: side menu switch
     * */
    useEffect(() => {
        setShowChatPanel(
            chatEnabled == true && lobbyChatEnable == true && isChatPanelEnabled
        );
    }, [lobbyChatEnable, chatEnabled, isChatPanelEnabled]);

    const sendMessage = (message: string) => {
        if (!message) {
            return false;
        } else if (IsTrial) {
            addErrorMessage(t('trial_account'));
            return false;
        } else if (!allowInput.current) {
            addErrorMessage(t('chat_room.chat_room_wait_msg'));
            return false;
        }
        setTimeout(() => {
            allowInput.current = true;
        }, inputIntervalTime);
        allowInput.current = false;
        let msg = {
            request_msg_type: MSG_TYPE.SendMsg,
            player_name: Username,
            player_id: PlayerID,
            token: LoginToken.slice(0, 16),
            msg: message,
        };
        console.log('chatroom::chat::send', msg);
        if (
            giftSocket.current &&
            giftSocket.current.readyState == WebSocket.OPEN
        ) {
            giftSocket.current.send(JSON.stringify(msg));
        }
        return true;
    };

    const removeMsg = useCallback((key?: string) => {
        setMsgDisplay(list => list?.filter(g => g.key != key));
    }, []);

    const addErrorMessage = (errorMsg: string, key?: string) => {
        let newMsg = {
            message: errorMsg,
            key: key ?? uuidv4(),
            self: true,
            error: true,
        };
        setMsgDisplay(displayList => [newMsg, ...displayList]);
    };

    const addMessage = (data: {
        sender_id: string;
        sender_name: string;
        display_msg: string;
        msg_ref_id: string;
        last_bet_time: string;
        is_forbidden_msg: boolean;
    }) => {
        const isSelf = PlayerID.toString() == data.sender_id;
        if (data.is_forbidden_msg) {
            isSelf && addErrorMessage(t('chat_room.inappropriate'));
            return;
        }
        const info = {
            message: `[${isSelf ? t('system.you').toUpperCase() : data.sender_name}] ${data.display_msg}`,
            self: isSelf,
            key: data.msg_ref_id,
        };
        setMsgDisplay(displayList => [info, ...displayList]);
    };

    const lastBetChecking = (data: {
        response_code: number;
        last_bet_time: string;
    }) => {
        const lastBetTime = Number(data.last_bet_time);
        const currTime = currentTimeStamp() / 1000 - 8 * 3600;
        switch (data.response_code) {
            case ERROR_TYPE.FailToSendMsgLastBetTimeError:
                allowInput.current = true;
                addErrorMessage(t('chat_room.place_bet'));
                break;
            case ERROR_TYPE.FailToSendMsgExceedSendLimit:
                addErrorMessage(t('chat_room.chat_room_wait_msg'));
                break;
            default:
                if (!lastBetTime || lastBetTime + chatTime < currTime) {
                    allowInput.current = true;
                    addErrorMessage(t('chat_room.place_bet'));
                }
                break;
        }
    };

    const chatServerChecking = (data: {
        is_chat_control_server_healthy: boolean;
    }) => {
        data &&
            setChatConnected(
                data.is_chat_control_server_healthy ? true : false
            );
    };

    // useEffect(() => {
    //     if (
    //         msgPendingList &&
    //         msgPendingList.current.length > 0
    //     ) {
    //         addMsgToDisplay();
    //     }
    // }, [changeNum]);

    return {
        disable,
        chatConfig,
        sendMessage,
        placeholder,
        hideSendBtn,
        msgDisplay,
        removeMsg,
        onFocus,
        showChatPanel,
        addErrorMessage,
        allowSendMsg,
        pendingGifts,
        setPendingGifts,
        giftsDisplay,
        setGiftsDisplay,
        socketClose,
        IsTrial,
    };
};
export default useChatRoom;
export type useChatRoomState = ReturnType<typeof useChatRoom>;
